import { Chip } from "@gigpro/ui/Chip";
import type { InvoicingRunTypeEnum } from "@sidegigllc/api/client";
import { BACKFILL_CHIP_CLASS } from "./backfill";

/** Both SCHEDULED_* cadences deliberately collapse to a single label. Daily vs
 * weekly is already legible from the Period column, and the question ops is
 * asking at this glance is binary: did this run come out of the normal
 * pay-period pipeline, or is it a retroactive sweep? Splitting this into three
 * labels re-answers a question the row already answers and dilutes the one it
 * does not — so do not "fix" it into one label per enum value.
 *
 * Typed ``Record<InvoicingRunTypeEnum, string>`` so a future cadence
 * (SCHEDULED_BIWEEKLY) fails the typecheck instead of rendering a blank chip. */
const RUN_TYPE_LABELS: Record<InvoicingRunTypeEnum, string> = {
  SCHEDULED_DAILY: "Pay period",
  SCHEDULED_WEEKLY: "Pay period",
  POST_INVOICE_ADHOC: "Ad hoc",
  BACKFILL_SCHEDULED_DAILY: "Backfill",
  BACKFILL_SCHEDULED_WEEKLY: "Backfill",
  BACKFILL_POST_INVOICE_ADHOC: "Backfill",
};

/** The live type each BACKFILL_ type mirrors, and `null` for the live types
 * themselves. All three backfills collapse to one "Backfill" chip in the queue
 * (the question there is "can ops act on this?"), but the detail page names
 * which pipeline the mirrored lines would otherwise have come from.
 *
 * Exhaustive for the same reason RUN_TYPE_LABELS is: a fourth backfill type
 * has to say what it mirrors rather than lose its sub-kind silently. */
const BACKFILL_MIRRORS: Record<InvoicingRunTypeEnum, InvoicingRunTypeEnum | null> = {
  SCHEDULED_DAILY: null,
  SCHEDULED_WEEKLY: null,
  POST_INVOICE_ADHOC: null,
  BACKFILL_SCHEDULED_DAILY: "SCHEDULED_DAILY",
  BACKFILL_SCHEDULED_WEEKLY: "SCHEDULED_WEEKLY",
  BACKFILL_POST_INVOICE_ADHOC: "POST_INVOICE_ADHOC",
};

/** The Type filter's buckets. These are UI-level values, not run types — the
 * URL carries `?type=PAY_PERIOD`, never a raw enum value. A param holding a
 * single enum value could not express "Pay period", which is two of them, so a
 * filter built in the 1:1 shape of the `invoice_style` filter beside it would
 * silently drop every SCHEDULED_DAILY row. */
export const RUN_TYPE_FILTERS = ["PAY_PERIOD", "ADHOC", "BACKFILL"] as const;
export type RunTypeFilter = (typeof RUN_TYPE_FILTERS)[number];

/** The one place the many-to-one run type → bucket mapping lives, kept beside
 * RUN_TYPE_LABELS so the two cannot drift. Exhaustive for the same reason the
 * label map is: a cadence added later has to be bucketed deliberately, rather
 * than matching neither option and vanishing from the queue whenever ops
 * filters at all.
 *
 * "Backfill" is many-to-one in the same way "Pay period" is: three run types,
 * one bucket. A backfill of an ad-hoc sweep is a backfill first, so it does
 * not also match "Ad hoc". */
export const RUN_TYPE_FILTER_BUCKETS: Record<InvoicingRunTypeEnum, RunTypeFilter> = {
  SCHEDULED_DAILY: "PAY_PERIOD",
  SCHEDULED_WEEKLY: "PAY_PERIOD",
  POST_INVOICE_ADHOC: "ADHOC",
  BACKFILL_SCHEDULED_DAILY: "BACKFILL",
  BACKFILL_SCHEDULED_WEEKLY: "BACKFILL",
  BACKFILL_POST_INVOICE_ADHOC: "BACKFILL",
};

/** Options for the Type column's filter. Labels are read off RUN_TYPE_LABELS
 * rather than retyped, so an option can never read differently from the chip
 * it selects for. */
export const RUN_TYPE_FILTER_OPTIONS: ReadonlyArray<{ value: RunTypeFilter; label: string }> = [
  { value: "PAY_PERIOD", label: RUN_TYPE_LABELS.SCHEDULED_WEEKLY },
  { value: "ADHOC", label: RUN_TYPE_LABELS.POST_INVOICE_ADHOC },
  { value: "BACKFILL", label: RUN_TYPE_LABELS.BACKFILL_SCHEDULED_WEEKLY },
];

interface Props {
  type: InvoicingRunTypeEnum;
  /** Name what a backfill mirrors beside it ("Backfill · Pay period"). For the
   * detail header, where there is room; the queue keeps the one-word chip. */
  withSubKind?: boolean;
}

/** Names why a run exists. A retroactive ad-hoc sweep is single-day, which is
 * exactly what a legitimate SCHEDULED_DAILY run looks like, so the period alone
 * cannot separate them.
 *
 * Ad hoc takes the brand tint — the same signal the "Manual" line-item chip
 * uses for a row that did not come from the normal pipeline. Backfill takes the
 * purple its status badge carries, since a BACKFILL type only ever travels with
 * the BACKFILL status. Pay period keeps the neutral Chip default: the expected
 * case should not ask for a second look. */
export function RunTypeChip({ type, withSubKind = false }: Readonly<Props>) {
  const mirrored = BACKFILL_MIRRORS[type];
  const label =
    withSubKind && mirrored ? `${RUN_TYPE_LABELS[type]} · ${RUN_TYPE_LABELS[mirrored]}` : RUN_TYPE_LABELS[type];

  let className: string | undefined;
  if (mirrored) className = BACKFILL_CHIP_CLASS;
  else if (type === "POST_INVOICE_ADHOC") className = "bg-brand/15 text-brand";

  return <Chip className={className}>{label}</Chip>;
}
